/**
 * @file sanitizeConditionalFields.ts
 * @description Reusable utility that nullifies form values for fields hidden by `conditionalFields`
 *              before the payload is submitted to the API.
 *
 * @module helpers/sanitizeConditionalFields
 *
 * ## Problem
 * When a user fills in a conditionally-visible field and then changes a dropdown so that
 * field becomes hidden, the value remains in `formik.values`. Without sanitization, stale
 * data would be sent to the API.
 *
 * ## How it works
 * 1. Iterates over every key in the `conditionalFields` config.
 * 2. For each field, re-evaluates the same show/hide logic used by Entity's
 *    `filterGroupedMetaData` (including `datasourceField` internalCode resolution).
 * 3. If a field evaluates to "hidden", its value in the returned payload is set to `null`.
 * 4. The original values object is **never mutated** — a shallow copy is returned.
 *
 * ## Usage
 * ```ts
 * import { sanitizeConditionalFieldValues } from "helpers/sanitizeConditionalFields";
 *
 * const cleanPayload = sanitizeConditionalFieldValues(formik.values, conditionalFields);
 * // cleanPayload has hidden field values set to null
 * ```
 *
 * @example
 * // Given conditionalFields:
 * const conditionalFields = {
 *   Compliment: {
 *     dependentField: "SupplierFeedbackTypesID",
 *     dependentValues: ["EXCEEDED_EXPECTATIONS"],
 *     datasourceField: "listSupplierFeedbackTypes",
 *   },
 *   NCRCategoryTypesID: {
 *     dependentField: "SupplierFeedbackTypesID",
 *     dependentValues: ["DID_NOT_MEET_EXPECTATIONS"],
 *     datasourceField: "listSupplierFeedbackTypes",
 *   },
 * };
 *
 * // If supplierFeedbackTypesID currently maps to EXCEEDED_EXPECTATIONS:
 * //   → compliment is VISIBLE  → value preserved
 * //   → ncrCategoryTypesID is HIDDEN → value set to null
 */

import { ConditionalFields } from "components/Entity/Entity";

/**
 * Converts a PascalCase string to camelCase, matching .NET's JSON serialization behavior.
 *
 * Handles consecutive uppercase letters (acronyms) correctly:
 * - `NCRCategoryTypesID` → `ncrCategoryTypesID`
 * - `NCRDescription`     → `ncrDescription`
 * - `Compliment`         → `compliment`
 * - `BMilestone`         → `bMilestone`
 *
 * Algorithm: find the leading uppercase run. If the run length > 1 and is followed
 * by a lowercase letter, lowercase all characters except the last in the run.
 * If the run reaches the end of the string (e.g. "ID"), lowercase the entire run.
 */
export const toCamelCase = (str: string): string => {
  if (!str) return str;

  // Find the length of the leading uppercase run
  let upperRunLength = 0;
  for (let i = 0; i < str.length; i++) {
    if (str[i] === str[i].toUpperCase() && str[i] !== str[i].toLowerCase()) {
      upperRunLength++;
    } else {
      break;
    }
  }

  if (upperRunLength === 0) return str; // already camelCase
  if (upperRunLength === 1) return str.charAt(0).toLowerCase() + str.slice(1);

  // Acronym: lowercase all except the last uppercase char (which starts the next word)
  // Unless the run goes to the end of the string, then lowercase everything
  if (upperRunLength === str.length) {
    return str.toLowerCase();
  }

  return str.slice(0, upperRunLength - 1).toLowerCase() + str.slice(upperRunLength - 1);
};

/**
 * Determines whether a single conditional field should be visible given the current form values.
 *
 * Mirrors the logic in Entity's `filterGroupedMetaData` function:
 * 1. If `datasourceField` is specified, looks up the datasource array in `values`,
 *    and if absent there, falls back to `externalDatasource` (e.g. Entity's `formDatasourceData`
 *    snapshot). This handles the multipart form case where datasource arrays are stripped from
 *    `formik.values` before submission but are still available in the external snapshot.
 * 2. Otherwise, compares the raw field value directly against `dependentValues`.
 * 3. If `dependentType` is set, checks `typeof fieldValue === dependentType`.
 *
 * @param values             - Current form values object (e.g. `formik.values`)
 * @param fieldConfig        - The conditional field configuration entry
 * @param externalDatasource - Optional fallback datasource map (e.g. Entity's `formDatasourceData`)
 * @returns `true` if the field should be visible, `false` if it should be hidden
 */
export const isConditionalFieldVisible = (
  values: Record<string, any>,
  fieldConfig: ConditionalFields[string],
  externalDatasource?: Record<string, any>
): boolean => {
  // Lowercase the first character of the dependent field name to match formik value keys
  const dependentFieldName =
    fieldConfig.dependentField.charAt(0).toLowerCase() + fieldConfig.dependentField.slice(1);

  const fieldValue = values[dependentFieldName];
  const { dependentValues, datasourceField, dependentType } = fieldConfig;

  // --- Datasource path: resolve internalCode from the datasource array ---
  if (datasourceField) {
    // Prefer the datasource from formik.values; fall back to the external snapshot
    // (Entity's formDatasourceData) which is available even when multipart serialisation
    // has already stripped datasource arrays out of the submitted values object.
    const datasourceSource: Record<string, any> | null = values.hasOwnProperty(datasourceField)
      ? values
      : externalDatasource?.hasOwnProperty(datasourceField)
        ? externalDatasource
        : null;

    if (datasourceSource === null && process.env.NODE_ENV !== "production") {
      console.warn(
        `[sanitizeConditionalFields] Datasource field "${datasourceField}" not found in ` +
          `formik.values or externalDatasource. Visibility check for dependent field ` +
          `"${fieldConfig.dependentField}" will fall back to direct value comparison.`
      );
    }

    if (datasourceSource !== null) {
      const datasource = datasourceSource[datasourceField];

      if (
        Array.isArray(datasource) &&
        datasource.length > 0 &&
        typeof datasource[0] === "object" &&
        datasource[0].hasOwnProperty("internalCode") &&
        datasource[0].hasOwnProperty("value") &&
        typeof fieldValue === "number"
      ) {
        const match = datasource.filter((item: any) => item.value === fieldValue);

        if (
          Array.isArray(match) &&
          match.length === 1 &&
          match[0].hasOwnProperty("internalCode") &&
          typeof match[0].internalCode === "string"
        ) {
          const fieldValueCode = match[0].internalCode;
          if (dependentValues && dependentValues.includes(fieldValueCode)) {
            return true;
          }
        }

        // Datasource was found and resolved — a non-match here is definitive.
        // Return false immediately so we don't fall through to the direct value
        // path and accidentally match a raw numeric ID against internalCode strings.
        return false;
      }
    }
  }

  // --- Direct value path: compare raw value against dependentValues ---
  if (dependentValues && dependentValues.includes(fieldValue)) {
    return true;
  }

  // --- Type check path ---
  if (dependentType && typeof fieldValue === dependentType) {
    return true;
  }
  if (
    dependentType &&
    Array.isArray(fieldValue) &&
    fieldValue.length > 0 &&
    typeof fieldValue[0] === dependentType
  ) {
    return true;
  }

  return false;
};

/**
 * Returns a shallow copy of `values` with hidden conditional field values set to `null`.
 *
 * For each field defined in `conditionalFields`, this function evaluates whether the field
 * is currently visible. If it is hidden (i.e. the dependent field's value does not match
 * the configured `dependentValues`), the corresponding key in the returned object is set
 * to `null` to prevent stale data from being submitted to the API.
 *
 * **Important:** The original `values` object is never mutated.
 *
 * @param values             - Current form values (e.g. `formik.values`)
 * @param conditionalFields  - The `conditionalFields` config passed to `<Entity />`
 * @param externalDatasource - Optional fallback datasource map. Pass Entity's `formDatasourceData`
 *                             here so that datasource arrays (e.g. `listSupplierFeedbackTypes`)
 *                             can be resolved even when they are not present in `formik.values`
 *                             at submit time (which happens for multipart forms because Entity
 *                             strips datasource-array fields from the FormData payload).
 * @returns A new object with hidden field values nullified
 *
 * @example
 * // User selected EXCEEDED_EXPECTATIONS but had previously typed NCR data
 * const values = {
 *   supplierFeedbackTypesID: 1,
 *   compliment: "Great job!",
 *   ncrCategoryTypesID: 5,       // stale — field is hidden
 *   ncrDescription: "Was broken", // stale — field is hidden
 *   listSupplierFeedbackTypes: [
 *     { internalCode: "EXCEEDED_EXPECTATIONS", value: 1 },
 *     { internalCode: "DID_NOT_MEET_EXPECTATIONS", value: 2 },
 *   ],
 * };
 *
 * const clean = sanitizeConditionalFieldValues(values, conditionalFields);
 * // clean.compliment === "Great job!"   (preserved — field is visible)
 * // clean.ncrCategoryTypesID === null   (sanitized — field is hidden)
 * // clean.ncrDescription === null       (sanitized — field is hidden)
 *
 * @example
 * // Datasource missing from values but available in formDatasourceData snapshot
 * const clean = sanitizeConditionalFieldValues(values, conditionalFields, formDatasourceData);
 * // Sanitization still works correctly for fields whose datasource array was stripped from values
 */
export const sanitizeConditionalFieldValues = (
  values: Record<string, any>,
  conditionalFields: ConditionalFields,
  externalDatasource?: Record<string, any>
): Record<string, any> => {
  const sanitized = { ...values };

  Object.keys(conditionalFields).forEach((fieldName) => {
    const config = conditionalFields[fieldName];
    const visible = isConditionalFieldVisible(values, config, externalDatasource);

    if (!visible) {
      // Convert PascalCase field name to camelCase to match formik value keys
      const valueKey = toCamelCase(fieldName);
      sanitized[valueKey] = null;
    }
  });

  return sanitized;
};
